<template>
  <div
    class="app-dropdown"
    ref="wrapperRef"
    @mouseenter="openDropdown"
    @mouseleave="closeMouseDropdown($event)"
  >
    <div ref="triggerRef">
      <AppButton
        v-if="type == 'default'"
        :label="label"
        :disabled="disabled"
        :active="isOpen"
        :width="width"
        type="dropdown"
        @click="toggleDropdown"
      />
      <app-icon
        v-if="type == 'menu'"
        name="options-menu"
        width="16px"
        color="#979797"
        cursor="pointer"
        hover-color="#499259"
      />
    </div>
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="app-dropdown__window"
      :class="[`app-dropdown__window-${type}`]"
      :style="dropdownStyles"
    >
      <slot :onSelect="handleSelect" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch, type CSSProperties } from "vue";
import AppButton from "../ui-kit/button/AppButton.vue";
import AppIcon from "../ui-kit/icon/AppIcon.vue";

type DropdownProps = {
  position?: "left" | "right";
  label?: string;
  disabled?: boolean;
  width?: string;
  type: string;
};

type DropdownEmits = {
  "on-close": void;
  "select": [option: string];
};

const props = withDefaults(defineProps<DropdownProps>(), {
  position: "left",
  label: "Menu",
  disabled: false,
  width: "",
  type: "default",
});

const emit = defineEmits<DropdownEmits>();

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
let closeTimeout: ReturnType<typeof setTimeout> | null = null;
const dropdownStyles = ref<CSSProperties>({
  position: "fixed",
  top: "0px",
  left: "0px",
  minWidth: "0px",
  opacity: "0",
});
let openTimeout: ReturnType<typeof setTimeout> | null = null;

const handleSelect = (option: string) => {
  closeDropdown();
  emit("select", option);
};

watch(isOpen, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      updatePosition();
      window.addEventListener("scroll", handleScroll);
      document.addEventListener("click", handleClickOutside);
    }, 50);
  } else {
    window.removeEventListener("scroll", handleScroll);
    document.removeEventListener("click", handleClickOutside);
  }
});

const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value || !wrapperRef.value) return
  const target = event.target as HTMLElement

  if (target.closest('.dropdown-content')) return

  if (!wrapperRef.value.contains(target)) {
    closeDropdown()
  }
}

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("click", handleClickOutside);
  if (closeTimeout) {
    clearTimeout(closeTimeout);
  }
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const resetDropdownState = () => {
  if (openTimeout) {
    clearTimeout(openTimeout);
    openTimeout = null;
  }

  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }

  isOpen.value = false;
};

const openDropdown = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }

  if (openTimeout) {
    clearTimeout(openTimeout);
  }

  openTimeout = setTimeout(() => {
    isOpen.value = true;
    openTimeout = null;
  }, 1000);
};

const closeMouseDropdown = (event?: MouseEvent) => {
  if (openTimeout) { clearTimeout(openTimeout); openTimeout = null }
  if (closeTimeout) { clearTimeout(closeTimeout) }

  closeTimeout = setTimeout(() => {
    const hovered = document.querySelectorAll(':hover')
    const isOverDropdownContent = Array.from(hovered).some(
      el => el.classList.contains('dropdown-content')
    )
    if (isOverDropdownContent) {
      closeTimeout = null
      return
    }
    isOpen.value = false
    closeTimeout = null
  }, 1000)
}

const closeDropdown = () => {
  resetDropdownState();
};

defineExpose({ closeDropdown });

const updatePosition = () => {
  const triggerEl = triggerRef.value;
  const dropdownEl = dropdownRef.value;

  if (!triggerEl || !dropdownEl) return;

  dropdownEl.style.visibility = "hidden";

  const triggerRect = triggerEl.getBoundingClientRect();
  const dropdownRect = dropdownEl.getBoundingClientRect();

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  const openDownward =
    spaceBelow >= dropdownRect.height ||
    (spaceBelow >= spaceAbove && spaceBelow > 50);
  const openUpward = !openDownward;

  let top: number;

  if (openDownward) {
    top = triggerRect.bottom + window.scrollY;

    if (top + dropdownRect.height > viewportHeight - 10) {
      top = viewportHeight - dropdownRect.height - 10;
    }

    if (props.type == "menu") {
      top -= 18;
    }
  } else {
    top = triggerRect.top + window.scrollY - dropdownRect.height;

    if (top < 10) {
      top = 10;
    }

    if (props.type == "menu") {
      top += 18;
    }
  }

  if (openDownward && props.type === "default") {
    top += 40;
  } else if (openUpward && props.type === "default") {
    top -= 40;
  }

  let left;

  if (props.position === "left") {
    left = triggerRect.left + window.scrollX;

    if (left + dropdownRect.width > viewportWidth - 10) {
      left = viewportWidth - dropdownRect.width - 10;
    }
    if (left < 10) {
      left = 10;
    }
  } else {
    left = triggerRect.right + window.scrollX - dropdownRect.width;
    if (left < 10) {
      left = 10;
    }
    if (left + dropdownRect.width > viewportWidth - 10) {
      left = viewportWidth - dropdownRect.width - 10;
    }
  }

  if (props.type === "menu") {
    left += 15;
  }

  dropdownEl.style.visibility = "visible";

  dropdownStyles.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${triggerRect.width}px`,
    opacity: "1",
  };

  dropdownEl.classList.toggle("app-dropdown__window--upward", openUpward);
  dropdownEl.classList.toggle("app-dropdown__window--downward", openDownward);
};

const handleScroll = () => {
  if (isOpen.value) {
    closeDropdown();
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      updatePosition();
      window.addEventListener("scroll", handleScroll);
    }, 50);
  } else {
    window.removeEventListener("scroll", handleScroll);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.app-dropdown {
  display: flex;
  flex-direction: column;
  // position: absolute;
  position: relative;
  gap: 8px;
  width: auto;

  &__window {
    border: 1px solid $color-border;
    border-radius: 8px;
    // padding: 8px;
    background-color: $background-white;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    width: max-content;
    // position: fixed;
    
    box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);

    // &-default {
    //   // margin-top: 40px;
    // }

    // &-menu {
    //   // margin-left: 15px;
    // }
  }
}
</style>
